import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ConnectForm_Payload_Type,
  connectForm,
  disconnectForm,
  getConnectedFormsByCompany,
} from '../axiosNetworkRequest/connectedForms';
import { GetFormsByCompany_Response_Type } from '../axiosNetworkRequest/forms';
import { useSelector } from 'react-redux';
import { ReduxRootStateType } from '../../redux/store';
import { track } from '../../utils/MixpanelHelper';
import useNotification from '../../hooks/useNotification';
import { API_Error_Type } from '../axiosClient/axiosClient';

const FIVE_MINUTES = 5 * 60 * 1000;

export const useGetConnectedFormsByCompany = (company_id: number) =>
  useQuery({
    queryKey: ['connected-forms-company', company_id],
    queryFn: () => getConnectedFormsByCompany(`connected-forms?company_id=${company_id}`),
    staleTime: FIVE_MINUTES,
    enabled: company_id > 0,
  });

export const useConnectForm = (company_id: number) => {
  const queryClient = useQueryClient();
  const userInfo = useSelector((state: ReduxRootStateType) => state.user.user_info);
  const { setMessage, setSeverity, handleSnackbarOpen } = useNotification();
  return useMutation({
    mutationFn: (payload: ConnectForm_Payload_Type) => connectForm(payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['connected-forms-company', company_id] });
      const formsCache = queryClient.getQueryData<GetFormsByCompany_Response_Type>(['forms-by-company', company_id]);
      const form       = formsCache?.data.find(f => f.form_id === variables.form_id);
      track('Form Attached to File', {
        'File Name':    variables.file_name,
        'Form Name':    form?.form_name ?? 'unknown',
        'Company Name': userInfo?.company_name,
        'Attached By':  `${userInfo?.first_name} ${userInfo?.last_name}`,
        'Email':        userInfo?.email_id,
      });
    },
    onError: (error) => {
      const err = error as unknown as API_Error_Type;
      setMessage(err.client_error_msg ?? 'Failed to attach form');
      setSeverity('error');
      handleSnackbarOpen();
    },
  });
};

export const useDisconnectForm = (company_id: number) => {
  const queryClient = useQueryClient();
  const { setMessage, setSeverity, handleSnackbarOpen } = useNotification();
  return useMutation({
    mutationFn: (connection_id: number) => disconnectForm(connection_id),
    onMutate: (connection_id) => {
      queryClient.setQueryData(
        ['connected-forms-company', company_id],
        (old: any) => old
          ? { ...old, data: old.data.filter((c: any) => c.connection_id !== connection_id) }
          : old
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['connected-forms-company', company_id] });
    },
    onError: (error) => {
      const err = error as unknown as API_Error_Type;
      setMessage(err.client_error_msg ?? 'Failed to detach form');
      setSeverity('error');
      handleSnackbarOpen();
    },
  });
};
